import { useRef, useCallback, useEffect } from "react";
import { useLang } from "@/i18n/LangContext";
import Spinner from "@/components/Spinner";
import { bbcodeToText } from "@/lib/bbcode";
import type { Review, ReviewSummary } from "@/types/api";
import { STEAM_LOCALES } from "@/lib/steam-locale";
import { getLocaleString } from "@/lib/format";

interface ReviewsSectionProps {
  appid: string;
  appidNum: number;
  reviews: Review[];
  reviewSummary: ReviewSummary | null;
  reviewLang: string;
  setReviewLang: (lang: string) => void;
  reviewsLoading: boolean;
  reviewsHasMore: boolean;
  reviewsSentinelCallback: (node: HTMLDivElement | null) => void;
  onLoadMore?: () => void;
}

export default function ReviewsSection({
  appid: _appid,
  appidNum: _appidNum,
  reviews,
  reviewSummary,
  reviewLang,
  setReviewLang,
  reviewsLoading,
  reviewsHasMore,
  reviewsSentinelCallback,
  onLoadMore,
}: ReviewsSectionProps) {
  const { lang, t } = useLang();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollThrottleRef = useRef(false);
  const prevScrollHeightRef = useRef(0);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || reviewsLoading || !reviewsHasMore || scrollThrottleRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      scrollThrottleRef.current = true;
      prevScrollHeightRef.current = el.scrollHeight;
      onLoadMore?.();
      setTimeout(() => { scrollThrottleRef.current = false; }, 1000);
    }
  }, [reviewsLoading, reviewsHasMore, onLoadMore]);

  // 새 리뷰 로드 후 스크롤 위치 복원
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || prevScrollHeightRef.current === 0) return;
    // 새 콘텐츠 추가 후 스크롤이 맨 아래로 튀는 것 방지
    const prevTop = el.scrollTop;
    requestAnimationFrame(() => {
      el.scrollTop = prevTop;
    });
    prevScrollHeightRef.current = 0;
  }, [reviews]);

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm mb-6 overflow-hidden">
      <div className="p-6 pb-4">
        {/* 헤더 */}
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.userReviews}</h2>
        </div>

        {/* 언어 필터 — 29개 언어 셀렉트박스 */}
        <div className="mb-4">
          <div className="relative w-full">
            <select
              value={reviewLang}
              onChange={(e) => setReviewLang(e.target.value)}
              className="w-full h-10 pl-9 pr-8 bg-inner-bg dark:bg-[#1e2d3d] hover:bg-[#e4e8ee] dark:hover:bg-[#2a3d52] border border-inner-border dark:border-card-border rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 outline-none cursor-pointer appearance-none transition-colors"
              aria-label={t.gameDetail.allLanguages}
            >
              <option value="all">{t.gameDetail.allLanguages}</option>
              {STEAM_LOCALES.map((l) => (
                <option key={l.steam} value={l.steam}>
                  {l.nativeName} ({l.name})
                </option>
              ))}
            </select>
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {/* 리뷰 요약 */}
        {reviewSummary && reviewSummary.total_reviews > 0 && (
          <div className="bg-inner-bg dark:bg-inner-bg rounded-xl border border-inner-border dark:border-card-border p-5 mb-5">
            <div className="flex items-center gap-6">
              {/* 긍정률 원형 */}
              <div className="shrink-0 flex flex-col items-center">
                <div className="relative w-20 h-20">
                  <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15.9" fill="none"
                      stroke={
                        reviewSummary.total_reviews > 0 && reviewSummary.total_positive / reviewSummary.total_reviews >= 0.7
                          ? "#10b981"
                          : reviewSummary.total_positive / reviewSummary.total_reviews >= 0.4
                            ? "#f59e0b"
                            : "#ef4444"
                      }
                      strokeWidth="3"
                      strokeDasharray={`${(reviewSummary.total_positive / reviewSummary.total_reviews) * 100} ${100 - (reviewSummary.total_positive / reviewSummary.total_reviews) * 100}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-black text-gray-900 dark:text-white">
                      {Math.round((reviewSummary.total_positive / reviewSummary.total_reviews) * 100)}%
                    </span>
                  </div>
                </div>
                <span className={`text-xs font-bold mt-1 ${
                  reviewSummary.total_positive / reviewSummary.total_reviews >= 0.7
                    ? "text-emerald-600"
                    : reviewSummary.total_positive / reviewSummary.total_reviews >= 0.4
                      ? "text-amber-600"
                      : "text-red-600"
                }`}>
                  {reviewSummary.review_score_desc}
                </span>
              </div>

              {/* 통계 — 2행 (전체 위, 긍정/부정 아래) */}
              <div className="flex-1 flex flex-col gap-2 sm:gap-3">
                <div className="bg-card-bg dark:bg-card-bg rounded-lg p-2 sm:p-3 border border-inner-border dark:border-card-border">
                  <p className="text-[10px] sm:text-[11px] font-semibold text-gray-400 dark:text-muted uppercase tracking-wider mb-0.5">{t.gameDetail.totalReviews}</p>
                  <p className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white tabular-nums">{reviewSummary.total_reviews.toLocaleString(getLocaleString(lang))}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <div className="bg-card-bg dark:bg-card-bg rounded-lg p-2 sm:p-3 border border-inner-border dark:border-card-border">
                    <p className="text-[10px] sm:text-[11px] font-semibold text-gray-400 dark:text-muted uppercase tracking-wider mb-0.5">{t.gameDetail.positiveReviews}</p>
                    <p className="text-sm sm:text-lg font-bold text-blue-600 dark:text-blue-400 tabular-nums">{reviewSummary.total_positive.toLocaleString(getLocaleString(lang))}</p>
                  </div>
                  <div className="bg-card-bg dark:bg-card-bg rounded-lg p-2 sm:p-3 border border-inner-border dark:border-card-border">
                    <p className="text-[10px] sm:text-[11px] font-semibold text-gray-400 dark:text-muted uppercase tracking-wider mb-0.5">{t.gameDetail.negativeReviews}</p>
                    <p className="text-sm sm:text-lg font-bold text-pink-500 dark:text-pink-400 tabular-nums">{reviewSummary.total_negative.toLocaleString(getLocaleString(lang))}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 리뷰 목록 */}
        {reviews.length > 0 && (
          <div ref={scrollContainerRef} onScroll={handleScroll} className="flex flex-col gap-3 max-h-[600px] overflow-y-auto overscroll-contain pr-1">
            {reviews.map((review, idx) => (
              <div
                key={`${review.recommendationid}-${idx}`}
                className={`rounded-xl border p-4 ${
                  review.voted_up
                    ? "bg-[#f0f7ff] dark:bg-[#1c2a3d] border-blue-200 dark:border-blue-700/40"
                    : "bg-gray-50 dark:bg-[#232830] border-gray-200 dark:border-gray-600/40"
                }`}
              >
                {/* 상단: 추천/비추천 + 작성자 정보 */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    review.voted_up
                      ? "bg-blue-500 text-white"
                      : "bg-gray-400 dark:bg-gray-500 text-white"
                  }`}>
                    {review.voted_up ? t.gameDetail.recommend : t.gameDetail.notRecommend}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {t.gameDetail.profile}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(review.timestamp_created * 1000).toLocaleDateString(getLocaleString(lang))}
                  </span>
                  {review.timestamp_updated !== review.timestamp_created && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                      ({t.gameDetail.edited})
                    </span>
                  )}
                </div>

                {/* 리뷰 본문 */}
                <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed line-clamp-4 whitespace-pre-wrap mb-3">
                  {bbcodeToText(review.review)}
                </p>

                {/* 하단: 상세 정보 */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] bg-hover-bg dark:bg-white/10 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium">
                    {t.gameDetail.totalHours} {Math.round(review.author.playtime_forever / 60)}{t.gameDetail.hoursUnit}
                  </span>
                  {review.author.playtime_at_review > 0 && (
                    <span className="text-[10px] bg-hover-bg dark:bg-white/10 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium">
                      {t.gameDetail.atReview} {Math.round(review.author.playtime_at_review / 60)}{t.gameDetail.hoursUnit}
                    </span>
                  )}
                  {review.author.playtime_last_two_weeks > 0 && (
                    <span className="text-[10px] bg-blue-50 dark:bg-blue-500/10 text-blue-500 dark:text-blue-400 px-1.5 py-0.5 rounded font-medium">
                      {t.gameDetail.last2Weeks} {Math.round(review.author.playtime_last_two_weeks / 60)}{t.gameDetail.hoursUnit}
                    </span>
                  )}
                  {review.author.num_games_owned > 0 && (
                    <span className="text-[10px] bg-hover-bg dark:bg-white/10 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium">
                      {t.gameDetail.owned} {review.author.num_games_owned}{t.gameDetail.gamesUnit}
                    </span>
                  )}
                  {review.author.num_reviews > 0 && (
                    <span className="text-[10px] bg-hover-bg dark:bg-white/10 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium">
                      {t.gameDetail.reviewsWritten} {review.author.num_reviews}{t.gameDetail.writtenUnit}
                    </span>
                  )}
                  {review.steam_purchase && (
                    <span className="text-[10px] bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded font-medium">
                      {t.gameDetail.steamPurchase}
                    </span>
                  )}
                  {review.received_for_free && (
                    <span className="text-[10px] bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded font-medium">
                      {t.gameDetail.receivedFree}
                    </span>
                  )}
                  {review.written_during_early_access && (
                    <span className="text-[10px] bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 px-1.5 py-0.5 rounded font-medium">
                      {t.gameDetail.earlyAccess}
                    </span>
                  )}
                  {review.primarily_steam_deck && (
                    <span className="text-[10px] bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded font-medium">
                      Steam Deck
                    </span>
                  )}
                  {review.language && review.language !== "koreana" && (
                    <span className="text-[10px] bg-hover-bg dark:bg-white/10 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium">
                      {review.language}
                    </span>
                  )}

                  {/* 오른쪽: 반응 */}
                  <div className="ml-auto flex items-center gap-2">
                    {review.votes_up > 0 && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                        {t.gameDetail.helpful} {review.votes_up}
                      </span>
                    )}
                    {review.votes_funny > 0 && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                        {t.gameDetail.funny} {review.votes_funny}
                      </span>
                    )}
                    {review.comment_count > 0 && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                        {t.gameDetail.comments} {review.comment_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {/* 무한스크롤 트리거 */}
            <div ref={reviewsSentinelCallback} className="py-4 flex justify-center">
              {reviewsLoading && (
                <Spinner />
              )}
              {!reviewsHasMore && reviews.length > 20 && (
                <p className="text-xs text-gray-400 dark:text-muted">{t.gameDetail.allReviewsLoaded}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
